import type { BookGeometry } from '../interfaces/book-data.interface';
import type { DrawableData } from '../interfaces/drawable-data.interface';
import type { PrintSizes } from '../interfaces/print-sizes.interface';
import type { SpreadData } from '../interfaces/spread-data.interface';
import { getCoverPrintSizes, getPrintSizes, isCoverPrintSizes } from './book-queries';
import { mapSpreadDrawablesDeep } from './drawable-tree';
import { visibleRotation } from './spread-text-rect';

/** One spread canvas for screen and print, sized from the book (`width/height`, cover `cover_sizes`), never a background. */

export type SpreadCanvasKind = 'spread' | 'cover';

export interface CanvasPageFill {
  readonly x: number;
  readonly width: number;
}

export interface CanvasTrimBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Wire keys are snake_case like the rest of the book's size fields (`cover_sizes`, `cut_horizontal`). */
export interface SpreadCanvasGeometry {
  readonly viewport: { readonly width: number; readonly height: number };
  readonly spine_width: number;
  /** Render-space x of each collage (page); the right page starts past the left page and the spine. */
  readonly page_offsets: readonly number[];
  /** Page rectangle each collage's `fill` paints; the cover's spine belongs to the left page. */
  readonly page_fills: readonly CanvasPageFill[];
}

/** The print envelope of a spread: its canvas plus the printed page and where the size came from. */
export interface PrintCanvasGeometry extends SpreadCanvasGeometry {
  /** The page left after the cutter (sheet minus cut); `null` on the cover and when the cut is unknown. */
  readonly trim: CanvasTrimBox | null;
  readonly size_source: 'book' | 'cover_sizes';
}

/** The sheet of a spread or of the cover — the one choice the editor repeated inline at every canvas. */
export function sheetPrintSizes(book: BookGeometry, kind: SpreadCanvasKind): PrintSizes {
  return kind === 'cover' ? getCoverPrintSizes(book) : getPrintSizes(book);
}

/** Right page x: (W − S)/2 + S = (W + S)/2, S = spine (0 off the cover). */
function rightPageOffset(printSizes: PrintSizes): number {
  const spineWidth = isCoverPrintSizes(printSizes) ? printSizes.spine_width : 0;
  return (printSizes.width - spineWidth) / 2 + spineWidth;
}

export function spreadCanvasGeometry(printSizes: PrintSizes, collageCount: number): SpreadCanvasGeometry {
  const right = rightPageOffset(printSizes);
  const page_offsets: number[] = [];
  const page_fills: CanvasPageFill[] = [];
  for (let index = 0; index < collageCount; index++) {
    page_offsets.push(index === 0 ? 0 : right);
    if (collageCount < 2) page_fills.push({ x: 0, width: printSizes.width });
    else page_fills.push(index === 0 ? { x: 0, width: right } : { x: right, width: printSizes.width - right });
  }

  return {
    viewport: { width: printSizes.width, height: printSizes.height },
    spine_width: isCoverPrintSizes(printSizes) ? printSizes.spine_width : 0,
    page_offsets,
    page_fills,
  };
}

/** Render-space x of one collage; collages past the second share the right page. */
export function spreadPageOffset(printSizes: PrintSizes, collageIndex: number): number {
  return collageIndex === 0 ? 0 : rightPageOffset(printSizes);
}

/**
 * The sheet minus a margin on every side. A margin that swallows the sheet is broken data, not an empty
 * page — the whole sheet stands instead.
 */
export function trimSheet(width: number, height: number, marginX: number, marginY: number): CanvasTrimBox {
  const trimWidth = width - marginX * 2;
  const trimHeight = height - marginY * 2;
  if (!(trimWidth > 0) || !(trimHeight > 0)) return { x: 0, y: 0, width, height };
  return { x: marginX, y: marginY, width: trimWidth, height: trimHeight };
}

export type SpreadCanvasGeometryErrorReason = 'book_size_missing' | 'cover_sizes_missing' | 'cover_sizes_invalid';

export class SpreadCanvasGeometryError extends Error {
  constructor(
    readonly reason: SpreadCanvasGeometryErrorReason,
    message: string,
  ) {
    super(message);
    this.name = 'SpreadCanvasGeometryError';
  }
}

/** Only the size fields — the raw stored model (v0 or v1) and every wire shape carry them at the top level. */
export interface CanvasGeometryBookSizes {
  readonly width?: number | null;
  readonly height?: number | null;
  readonly cut_horizontal?: number | null;
  readonly cut_vertical?: number | null;
  readonly cover_sizes?: {
    readonly width?: number | null;
    readonly height?: number | null;
    readonly spine_width?: number | null;
  } | null;
}

function isSize(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * The strict entry of producers: no size — a named error, never a fallback to a background or to the other
 * sheet. A missing spine counts as 0, exactly as the screen reads it.
 */
export function resolvePrintCanvasGeometry(
  book: CanvasGeometryBookSizes,
  kind: SpreadCanvasKind,
  collageCount: number,
): PrintCanvasGeometry {
  if (kind === 'cover') {
    const cs = book.cover_sizes;
    if (!cs) throw new SpreadCanvasGeometryError('cover_sizes_missing', 'cover has no cover_sizes');
    if (!isSize(cs.width) || !isSize(cs.height)) {
      throw new SpreadCanvasGeometryError(
        'cover_sizes_invalid',
        `cover_sizes ${String(cs.width)}×${String(cs.height)} is not a sheet`,
      );
    }

    const sheet = { width: cs.width, height: cs.height, spine_width: isFiniteNumber(cs.spine_width) ? cs.spine_width : 0 };
    return {
      ...spreadCanvasGeometry(sheet as unknown as PrintSizes, collageCount),
      trim: null,
      size_source: 'cover_sizes',
    };
  }

  if (!isSize(book.width) || !isSize(book.height)) {
    throw new SpreadCanvasGeometryError(
      'book_size_missing',
      `book size ${String(book.width)}×${String(book.height)} is not a sheet`,
    );
  }

  const cutKnown = isFiniteNumber(book.cut_horizontal) && isFiniteNumber(book.cut_vertical);
  return {
    ...spreadCanvasGeometry({ width: book.width, height: book.height } as PrintSizes, collageCount),
    trim: cutKnown ? trimSheet(book.width, book.height, book.cut_horizontal as number, book.cut_vertical as number) : null,
    size_source: 'book',
  };
}

type GeometrySpread = { readonly collages?: readonly unknown[] | null };

export type CanvasGeometryCarrier = { readonly canvas_geometry?: PrintCanvasGeometry };

type WithGeometry<S> = S extends GeometrySpread ? S & CanvasGeometryCarrier : S;

export interface CanvasGeometryBook extends CanvasGeometryBookSizes {
  readonly spreads?: readonly GeometrySpread[] | null;
  readonly cover?: GeometrySpread | null;
}

/** A book as it goes to the renderer: every spread and the cover carry their canvas. */
export type WithCanvasGeometry<B extends CanvasGeometryBook> = Omit<B, 'spreads' | 'cover'> & {
  readonly spreads: WithGeometry<NonNullable<B['spreads']>[number]>[];
  readonly cover: WithGeometry<B['cover']>;
};

export interface CanvasGeometryFailure {
  readonly target: SpreadCanvasKind;
  /** Spread index in `spreads`; `null` for the cover. */
  readonly index: number | null;
  readonly reason: SpreadCanvasGeometryErrorReason;
  readonly message: string;
}

// `rotation` = the frame's own turn; a photo's turn inside it (`naturalAligns.image.angle`) stays put.
const rotationInContainer = <T extends DrawableData>(drawable: T): T =>
  drawable.rotation
    ? {
        ...drawable,
        rotation: 0,
        naturalAligns: {
          ...drawable.naturalAligns,
          container: { ...drawable.naturalAligns.container, angle: visibleRotation(drawable) },
        },
      }
    : drawable;

/**
 * Attaches `canvas_geometry` to every spread and to the cover of a renderer-bound book. A sheet without
 * sizes gets no geometry and is reported. What happens next is the producer's and the renderer's: a spread
 * sent without it fails in the renderer with a named error, a producer may drop such a cover before sending,
 * and the renderer skips an optional cover it cannot draw.
 * Legacy drawables (`collages[].drawableObjects`) get their `rotation` folded into `naturalAligns.container.angle`:
 * the renderer reads `container.angle` alone, the editor `rotation + container.angle`. Otherwise shape-agnostic —
 * reads only the size fields and the collage count; a boxes-wire collage goes through untouched.
 */
export function attachSpreadCanvasGeometry<B extends CanvasGeometryBook>(
  book: B,
  onFailure: (failure: CanvasGeometryFailure) => void = () => undefined,
): WithCanvasGeometry<B> {
  const withGeometry = <S extends GeometrySpread>(raw: S, kind: SpreadCanvasKind, index: number | null): S => {
    const spread = mapSpreadDrawablesDeep(raw as unknown as Pick<SpreadData, 'collages'>, rotationInContainer) as unknown as S;
    const collageCount = Array.isArray(spread.collages) ? spread.collages.length : 0;
    try {
      return { ...spread, canvas_geometry: resolvePrintCanvasGeometry(book, kind, collageCount) };
    } catch (error) {
      if (!(error instanceof SpreadCanvasGeometryError)) throw error;
      onFailure({ target: kind, index, reason: error.reason, message: error.message });
      return spread;
    }
  };

  // Only keys the book has are rewritten: a producer that dropped the cover (no cover to print) keeps it dropped.
  return {
    ...book,
    ...(Array.isArray(book.spreads)
      ? { spreads: book.spreads.map((spread, index) => withGeometry(spread, 'spread', index)) }
      : {}),
    ...(book.cover ? { cover: withGeometry(book.cover, 'cover', null) } : {}),
  } as unknown as WithCanvasGeometry<B>;
}

/** The producer's logger, structurally: photobook-model takes no logging dependency. */
export interface CanvasGeometryWarnLogger {
  warn(entry: { message: string; context: string; data: Record<string, unknown> }): unknown;
}

/** One warn per book naming the sheets that went out without a canvas; silent when there are none. */
export function warnCanvasGeometryFailures(
  logger: CanvasGeometryWarnLogger,
  context: string,
  data: Record<string, unknown>,
  failures: readonly CanvasGeometryFailure[],
): void {
  if (!failures.length) return;
  logger.warn({
    message: 'spread canvas has no print size — canvas_geometry is not attached to these sheets',
    context,
    data: { ...data, failures: failures.map(({ target, index, reason }) => ({ target, index, reason })) },
  });
}
